import { useState } from 'react';
import {
  AppButton,
  AppEmptyState,
  AppFormDialog,
  AppMenuSheet,
  AppPickerRow,
  AppScreenScaffold,
  AppSearchField,
  AppTextField,
  AppTopBar,
  AppUndoSnackbar,
  IconButton,
} from '@core/designsystem';
import { DOCUMENT_TYPES, typeLabel } from './formatter';
import { PdfView } from './PdfView';
import { WordView } from './WordView';
import type { ViewerActions, ViewerState } from './useViewer';
import './viewer.css';

/**
 * Просмотр документа.
 *
 * Единственный экран, где нет нижней навигации ([D-35](docs/DECISIONS.md)): документ
 * занимает экран целиком, и это единственное, чем здесь заняты руки.
 */
export function ViewerScreen({
  state,
  actions,
  onBack,
}: {
  state: ViewerState;
  actions: ViewerActions;
  onBack: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [typing, setTyping] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [broken, setBroken] = useState(false);
  const [pickingClient, setPickingClient] = useState(false);
  const [clientQuery, setClientQuery] = useState('');

  return (
    <AppScreenScaffold
      topBar={
        <AppTopBar
          title={state.title}
          subtitle={state.subtitle}
          onBack={onBack}
          actions={
            <>
              <IconButton
                icon="star"
                filled={state.isFavorite}
                label={state.isFavorite ? 'Убрать из избранного' : 'В избранное'}
                onClick={() => void actions.toggleFavorite()}
                className={state.isFavorite ? 'viewer__star--on' : undefined}
              />
              <IconButton icon="share" label="Поделиться" onClick={() => actions.share()} />
              <IconButton icon="more_vert" label="Ещё" onClick={() => setMenuOpen(true)} />
            </>
          }
        />
      }
      overlay={
        <>
          {menuOpen ? (
            <AppMenuSheet
              title={state.title}
              onDismiss={() => setMenuOpen(false)}
              items={[
                { icon: 'edit', label: 'Переименовать', onSelect: () => setRenaming(true) },
                {
                  icon: 'label',
                  label: `Тип документа — ${state.typeLabel}`,
                  onSelect: () => setTyping(true),
                },
                {
                  icon: 'person',
                  label: state.clientName
                    ? `Клиент — ${state.clientName}`
                    : 'Клиент документа',
                  onSelect: () => setPickingClient(true),
                },
                {
                  icon: 'upload_file',
                  label: 'Заменить файл',
                  onSelect: () => undefined,
                  picker: { multiple: false, onFiles: (files) => setReplacingFile(files) },
                },
                {
                  icon: 'delete',
                  label: 'Удалить',
                  danger: true,
                  onSelect: () => void actions.remove(),
                },
              ]}
            />
          ) : null}

          {/*
            Чей это документ: паспорт и доверенность относятся к человеку, а не к делу,
            и видны потом в его карточке. С поиском — клиентов у юриста сотни.
          */}
          {pickingClient ? (
            <AppFormDialog title="Клиент документа" onDismiss={() => setPickingClient(false)}>
              <AppSearchField
                value={clientQuery}
                onChange={setClientQuery}
                placeholder="Поиск клиента"
              />
              <div className="dialog__list">
                <AppPickerRow
                  label="Без клиента"
                  value=""
                  onClick={() => {
                    void actions.setClient(null);
                    setPickingClient(false);
                  }}
                />
                {state.clients
                  .filter((client) =>
                    client.name.toLowerCase().includes(clientQuery.trim().toLowerCase()),
                  )
                  .map((client) => (
                    <AppPickerRow
                      key={client.id}
                      label={client.name}
                      value={client.id === state.clientId ? 'выбран' : ''}
                      onClick={() => {
                        void actions.setClient(client.id);
                        setPickingClient(false);
                      }}
                    />
                  ))}
              </div>
            </AppFormDialog>
          ) : null}

          {renaming ? (
            <RenameDialog
              title={state.title}
              onDismiss={() => setRenaming(false)}
              onConfirm={(title) => {
                void actions.rename(title);
                setRenaming(false);
              }}
            />
          ) : null}

          {typing ? (
            <AppMenuSheet
              title="Тип документа"
              onDismiss={() => setTyping(false)}
              items={DOCUMENT_TYPES.map((type) => ({
                icon: 'label' as const,
                label: typeLabel(type),
                onSelect: () => void actions.setType(type),
              }))}
            />
          ) : null}

          {state.errorMessage ? (
            <AppUndoSnackbar
              text={state.errorMessage}
              actionLabel="Понятно"
              onUndo={actions.clearError}
              onExpire={actions.clearError}
            />
          ) : null}
        </>
      }
    >
      {content()}
    </AppScreenScaffold>
  );

  /** Замена файла приходит из системного окна пачкой из одного файла. */
  function setReplacingFile(files: File[]) {
    const next = files[0];
    if (!next) return;

    setReplacing(true);
    void actions.replaceContent(next).finally(() => setReplacing(false));
  }

  function content() {
    if (state.isDeleted) {
      return <AppEmptyState icon="delete" text="Документ удалён. «Отменить» — в списке дела" />;
    }
    if (state.notFound) return <AppEmptyState icon="error" text="Документ не найден" />;
    if (state.isLoading) return <div className="viewer__loading">Открываем документ…</div>;
    if (replacing) return <div className="viewer__loading">Меняем файл…</div>;

    const shown = state.content;
    if (!shown) return null;

    if (shown.kind === 'pdf' && !broken) {
      return <PdfView content={shown.content} onFailed={() => setBroken(true)} />;
    }

    if (shown.kind === 'word' && !broken) {
      return <WordView content={shown.content} onFailed={() => setBroken(true)} />;
    }

    if (shown.kind === 'image') {
      return (
        <div className="viewer__image-box">
          {/* Масштаб — системным жестом: свой зум на снимке хуже того, что уже есть в iOS. */}
          <img className="viewer__image" src={shown.url} alt={state.title} />
        </div>
      );
    }

    // Ни PDF, ни картинка, ни Word — либо файл не читается. Слова разные, выход один
    // и тот же: отдать файл тому приложению, которое умеет его открыть.
    const isBroken = broken || (shown.kind === 'external' && shown.broken);
    return (
      <AppEmptyState
        icon={isBroken ? 'error' : 'description'}
        text={
          isBroken
            ? 'Файл не открывается. Его можно отправить в другое приложение'
            : 'Такой файл открывает другое приложение'
        }
        action={<AppButton onClick={() => actions.share()}>Поделиться</AppButton>}
      />
    );
  }
}

function RenameDialog({
  title,
  onDismiss,
  onConfirm,
}: {
  title: string;
  onDismiss: () => void;
  onConfirm: (title: string) => void;
}) {
  const [value, setValue] = useState(title);

  return (
    <AppFormDialog
      title="Переименовать"
      onDismiss={onDismiss}
      confirm={{
        label: 'Готово',
        enabled: value.trim().length > 0,
        onClick: () => onConfirm(value),
      }}
    >
      <AppTextField value={value} onChange={setValue} label="Название" autoFocus />
    </AppFormDialog>
  );
}
